import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  List,
  ListItemIcon,
  ListItem,
  ListItemText,
  Typography,
  Collapse,
  Box
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ArrowDropUp";
import ExpandMore from "@mui/icons-material/ArrowDropDown";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  nestedMenuItem: {
    paddingLeft: theme.sizes.sideNavBarWidthCondensed,
    paddingRight: theme.spacing(1)
  },
  collapsedMenu: {
    backgroundColor: theme.palette.secondary[50]
  },
  listItem: {
    paddingRight: theme.spacing(2)
  },
  icon: {
    color: "inherit"
  },
  subtitle: {
    fontSize: "0.625rem",
    fontStyle: "italic",
    fontWeight: 600,
    paddingLeft: theme.spacing(1.25),
    color: theme.palette.primary.main
  }
}));

function checkIfActive(navItem, currentPath) {
  return navItem.children
    ? navItem.children.some(
        childItem => childItem.path && currentPath === childItem.path
      )
    : navItem.path === currentPath;
}

function SideNavItem(props) {
  const classes = useStyles();
  const {
    item,
    onMenuNavItemClick,
    menuExpanded,
    onMenuExpand,
    currentPath
  } = props;
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  React.useEffect(() => {
    if (!menuExpanded) {
      setExpanded(false);
    }
  });

  function handleMenuItemClick(event, itemClicked) {
    // If menu is not expanded and a nav item with children is clicked, expand the menu
    if (itemClicked.children) {
      if (!menuExpanded) {
        onMenuExpand(event, itemClicked);
      }
      setExpanded(!expanded);
    } else {
      onMenuNavItemClick(itemClicked);
    }
  }

  function getMenuItemContent() {
    return (
      <ListItem
        button
        onClick={e => handleMenuItemClick(e, item)}
        className={clsx(classes.listItem, {
          active: checkIfActive(item, currentPath)
        })}
        data-qa="nav-sidenav-menu-item"
      >
        <ListItemIcon className={classes.icon}>{item.icon}</ListItemIcon>
        <ListItemText
          primary={
            <Typography className="nav-item">{t(item.title)}</Typography>
          }
        />
        {item.children &&
          (expanded ? <ExpandLess color="primary" /> : <ExpandMore />)}
      </ListItem>
    );
  }

  function getMenuItemChildren() {
    if (item.children) {
      const nestedItems = item.children.filter(item => !item.hidden);
      return (
        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          classes={{ root: classes.collapsedMenu }}
        >
          <List component="div" disablePadding>
            {nestedItems.map((childItem, index) => (
              <ListItem
                button
                className={classes.nestedMenuItem}
                onClick={e => handleMenuItemClick(e, childItem)}
                key={index}
                data-qa={`${t(childItem.title)}-nav-sidenav-menu-item`}
              >
                <ListItemText
                  primary={
                    <Box display="inline-flex" alignItems="baseline">
                      <Typography className="nested-nav-item">
                        {t(childItem.title)}
                      </Typography>
                      {childItem.subtitle && (
                        <Typography className={classes.subtitle}>
                          {t(childItem.subtitle)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      );
    }
  }
  function getMenuItem() {
    if (item.path && !item.children) {
      return getMenuItemContent();
    }
    return getMenuItemContent();
  }
  return (
    <React.Fragment key={item.title}>
      {getMenuItem()}
      {item.children && getMenuItemChildren(item)}
    </React.Fragment>
  );
}

export default SideNavItem;

SideNavItem.propTypes = {
  item: PropTypes.any,
  currentPath: PropTypes.string,
  onMenuNavItemClick: PropTypes.func
};
